import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { where } from 'firebase/firestore'
import { useAuth } from '../lib/auth'
import { useCollection } from '../lib/firestore'
import { useEntitlements } from '../lib/entitlements'
import { useArtists, createArtist, updateArtist } from '../lib/artists'
import { formatDateRange } from '../lib/format'
import type { Artist, Tour, WithId } from '../types/models'
import Page from '../components/Page'
import Poster from '../components/Poster'
import Empty from '../components/Empty'
import Icon from '../components/Icon'
import Modal from '../components/Modal'
import { ErrorText, Field, Text } from '../components/Form'
import ImageDrop from '../components/ImageDrop'
import { useIsMobile } from '../lib/useMediaQuery'
import MobileHeader, { MobileScreen } from '../components/mobile/MobileHeader'

/* ============================================================================
   The roster.

   Every account has an artist — created at signup, named after them — so this
   screen is never empty. What changes with a roster is that it becomes worth
   opening: on one artist it is a profile, on twelve it is the thing an agency
   looks at every morning.

   The nav entry only appears once the plan allows more than one artist, so a
   solo user is never shown a page whose entire purpose is something they
   haven't bought.
   ============================================================================ */

export default function Artists() {
  const { user, uid } = useAuth()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { roster: artists, orgId, loading } = useArtists()
  const { plan, canAddArtist } = useEntitlements()
  const [creating, setCreating] = useState(false)

  const tours = useCollection<Tour>('tours', user ? [where('ownerId', '==', uid)] : [])

  const byArtist = useMemo(() => {
    const map = new Map<string, Array<WithId<Tour>>>()
    for (const tour of tours.data) {
      if (!tour.artistId) continue
      map.set(tour.artistId, [...(map.get(tour.artistId) ?? []), tour])
    }
    return map
  }, [tours.data])

  const allowed = canAddArtist(artists.length)

  const body = (
    <>
      {!allowed && (
        <p className="panel panel-pad t-caption" style={{ borderColor: 'var(--accent)' }}>
          {plan.displayName} covers{' '}
          {plan.maxArtists === 1 ? 'one artist' : `${plan.maxArtists} artists`}. Everything
          already here stays exactly as it is.
        </p>
      )}

      {loading && artists.length === 0 && <p className="t-caption">Loading…</p>}

      {!loading && artists.length === 0 && (
        <Empty
          title="No artists yet"
          message="An artist holds everything that survives between tours — the touring party, the rider, the history."
          action={allowed ? { label: 'Add an artist', onClick: () => setCreating(true) } : undefined}
        />
      )}

      <div
        className="responsive-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 14,
        }}
      >
        {artists.map((artist) => (
          <ArtistCard key={artist.id} artist={artist} tours={byArtist.get(artist.id) ?? []} />
        ))}
      </div>
    </>
  )

  return (
    <>
      {isMobile ? (
        <>
          <MobileHeader
            title="Artists"
            back={false}
            action={
              allowed
                ? { icon: 'plus', label: 'Add artist', accent: true, onClick: () => setCreating(true) }
                : undefined
            }
          />
          <MobileScreen>{body}</MobileScreen>
        </>
      ) : (
        <Page
          title="Artists"
          actions={
            <button
              className="btn btn-primary"
              disabled={!allowed}
              title={allowed ? undefined : `${plan.displayName} covers ${plan.maxArtists} artists.`}
              onClick={() => setCreating(true)}
            >
              Add artist
            </button>
          }
        >
          {body}
        </Page>
      )}

      {creating && orgId && (
        <ArtistDialog
          orgId={orgId}
          onClose={() => setCreating(false)}
          onCreated={(id) => navigate(`/artists/${id}`)}
        />
      )}
    </>
  )
}

/* ------------------------------------------------------------------- card */

function ArtistCard({
  artist,
  tours,
}: {
  artist: WithId<Artist>
  tours: Array<WithId<Tour>>
}) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const onTour = tours.find(
    (tour) => tour.startDate.toDate() <= today && tour.endDate.toDate() >= today,
  )
  const next = tours
    .filter((tour) => tour.startDate.toDate() > today)
    .sort((a, b) => a.startDate.toMillis() - b.startDate.toMillis())[0]

  return (
    <Link
      to={`/artists/${artist.id}`}
      className={`panel${onTour ? ' panel-signalled' : ''}`}
      style={{ display: 'block', padding: 16 }}
    >
      <div className="row" style={{ gap: 14, alignItems: 'flex-start' }}>
        <Poster url={artist.avatarURL} width={56} alt={`${artist.name} artwork`} />
        <div className="grow stack" style={{ gap: 4, minWidth: 0 }}>
          <span className="t-display-s truncate">{artist.name}</span>
          {/* The one thing an agency opens this page to learn. */}
          {onTour ? (
            <span className="chip chip-accent" style={{ alignSelf: 'flex-start' }}>
              On the road
            </span>
          ) : next ? (
            <span className="t-caption truncate">
              Next: {next.tourName} · {formatDateRange(next.startDate, next.endDate)}
            </span>
          ) : (
            <span className="t-caption">Nothing booked</span>
          )}
          <span className="t-spec-caption faint">
            {tours.length} tour{tours.length === 1 ? '' : 's'}
          </span>
        </div>
        <Icon name="chevronRight" size={16} style={{ color: 'var(--text-tertiary)' }} />
      </div>
    </Link>
  )
}

/* ----------------------------------------------------------------- dialog */

export function ArtistDialog({
  orgId,
  artist,
  onClose,
  onCreated,
}: {
  orgId: string
  artist?: WithId<Artist>
  onClose: () => void
  onCreated?: (artistId: string) => void
}) {
  const [name, setName] = useState(artist?.name ?? '')
  const [avatarURL, setAvatarURL] = useState(artist?.avatarURL ?? '')
  const [busy, setBusy] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  return (
    <Modal
      open
      title={artist ? 'Edit artist' : 'New artist'}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!name.trim() || busy || uploading}
            onClick={async () => {
              setBusy(true)
              setError(null)
              try {
                if (artist) {
                  await updateArtist(artist.id, { name, avatarURL })
                  onClose()
                } else {
                  const id = await createArtist({ name, avatarURL }, orgId)
                  onCreated?.(id)
                  onClose()
                }
              } catch (caught) {
                setError((caught as Error).message)
                setBusy(false)
              }
            }}
          >
            {uploading ? 'Uploading…' : busy ? 'Saving…' : artist ? 'Save' : 'Add artist'}
          </button>
        </>
      }
    >
      <div className="stack">
        <Field label="Name">
          <Text value={name} onChange={setName} autoFocus />
        </Field>
        <ImageDrop
          value={avatarURL}
          onChange={(url) => setAvatarURL(url ?? '')}
          onBusyChange={setUploading}
        />
        <ErrorText>{error}</ErrorText>
      </div>
    </Modal>
  )
}

